import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { RegisteredEmailOption, StatementEmailPayload } from "../types";
import { parseStatementEmails } from "../utils";

interface UseStatementEmailParams {
  customerId: string;
  customerData: any;
  customerEmail: string;
  tr: (key: string) => string;
  showSnackbar: (msg: string) => void;
}

const STORAGE_KEY = "statement_email_cooldowns";

// Module-level in-memory cache to ensure state persistence during app runtime
const inMemoryCooldowns: Record<string, number> = {};

export const loadStoredCooldowns = (): Record<string, number> => {
  const result: Record<string, number> = { ...inMemoryCooldowns };
  const now = Date.now();

  // Clean expired in-memory entries
  Object.keys(result).forEach((key) => {
    if (result[key] <= now) {
      delete result[key];
      delete inMemoryCooldowns[key];
    }
  });

  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        Object.entries(parsed).forEach(([email, expiry]) => {
          if (typeof expiry === "number" && expiry > now) {
            result[email] = expiry;
            inMemoryCooldowns[email] = expiry;
          }
        });
      }
    } catch {
      // Ignore localStorage read errors
    }
  }
  return result;
};

export const saveStoredCooldowns = (cooldowns: Record<string, number>) => {
  const now = Date.now();
  const valid: Record<string, number> = {};

  Object.entries(cooldowns).forEach(([email, expiry]) => {
    if (typeof expiry === "number" && expiry > now) {
      valid[email] = expiry;
      inMemoryCooldowns[email] = expiry;
    } else {
      delete inMemoryCooldowns[email];
    }
  });

  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(valid));
    } catch {
      // Ignore localStorage write errors
    }
  }
};

export function useStatementEmail({
  customerId,
  customerData,
  customerEmail,
  tr,
  showSnackbar,
}: UseStatementEmailParams) {
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [isEmailDropdownOpen, setIsEmailDropdownOpen] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);

  // Map of lowercase email to cooldown expiration timestamp (ms), initialized from storage
  const [emailCooldowns, setEmailCooldowns] = useState<Record<string, number>>(() =>
    loadStoredCooldowns()
  );
  const [currentTime, setCurrentTime] = useState<number>(() => Date.now());

  const emailDropdownRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLTextAreaElement>(null);

  // Re-sync with storage when selected email changes or component mounts
  useEffect(() => {
    const stored = loadStoredCooldowns();
    setEmailCooldowns(stored);
    setCurrentTime(Date.now());
  }, [selectedEmail]);

  // Check if any email currently has an active cooldown
  const hasActiveCooldown = useMemo(() => {
    return Object.values(emailCooldowns).some((expiry) => expiry > currentTime);
  }, [emailCooldowns, currentTime]);

  // Tick timer every second when cooldowns are active
  useEffect(() => {
    if (!hasActiveCooldown) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setCurrentTime(now);
    }, 1000);
    return () => clearInterval(timer);
  }, [hasActiveCooldown]);

  // Wait until every selected recipient is eligible for another statement.
  const recipientEmails = useMemo(() => parseStatementEmails(selectedEmail), [selectedEmail]);
  const cooldownExpiry = recipientEmails.reduce(
    (latest, email) => Math.max(latest, emailCooldowns[`${customerId}:${email.toLowerCase()}`] || 0),
    0
  );
  const remainingCooldownSeconds = Math.max(0, Math.ceil((cooldownExpiry - currentTime) / 1000));
  const isEmailCooldownActive = remainingCooldownSeconds > 0;

  // Format MM:SS or M:SS
  const formattedCooldownTime = useMemo(() => {
    if (remainingCooldownSeconds <= 0) return "";
    const minutes = Math.floor(remainingCooldownSeconds / 60);
    const seconds = remainingCooldownSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  }, [remainingCooldownSeconds]);

  // Collect all unique registered emails for this customer
  const customerRegisteredEmails = useMemo(() => {
    const list: RegisteredEmailOption[] = [];
    const seen = new Set<string>();

    const addEmail = (rawEmail?: string | null, label?: string, isDefault = false) => {
      if (!rawEmail) return;
      const email = rawEmail.trim();
      if (!email) return;
      const key = email.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      list.push({ email, label, isDefault });
    };

    // 1. Resolved primary default email
    if (customerEmail) {
      addEmail(customerEmail, `${customerData?.name || tr("Primary Contact")} (${tr("Default")})`, true);
    }

    // 2. Base customer email
    if (customerData?.email) {
      addEmail(customerData.email, customerData?.name || tr("Primary Contact"));
    }

    // 3. Extended contacts
    if (Array.isArray(customerData?.extended_contacts)) {
      customerData.extended_contacts.forEach((ext: any) => {
        if (ext.contact_type === "email" && ext.contact_value) {
          const desc = ext.contact_description ? ` (${ext.contact_description})` : "";
          const lbl = ext.is_primary
            ? `${tr("Primary Contact")}${desc} - ${tr("Default")}`
            : `${tr("Primary Contact")}${desc}`;
          addEmail(ext.contact_value, lbl, !!ext.is_primary);
        }
      });
    }

    // 4. Additional contacts
    if (Array.isArray(customerData?.contacts)) {
      customerData.contacts.forEach((contact: any) => {
        if (contact?.email) {
          const contactName =
            contact.name ||
            [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
            tr("Contact");
          const title = contact.title ? ` - ${contact.title}` : "";
          addEmail(contact.email, `${contactName}${title}`);
        }
      });
    }

    // 5. Billing emails
    if (Array.isArray(customerData?.billing_emails)) {
      customerData.billing_emails.forEach((email: string) => {
        addEmail(email, tr("Billing Email"));
      });
    }

    // 6. Additional emails
    if (Array.isArray(customerData?.additional_emails)) {
      customerData.additional_emails.forEach((email: string) => {
        addEmail(email, tr("Additional Email"));
      });
    }

    // 7. Primary contact info array
    if (Array.isArray(customerData?.primary_contact_info)) {
      customerData.primary_contact_info.forEach((info: any) => {
        if (info.contact_type === "email" && info.contact_value) {
          const desc = info.contact_description ? ` (${info.contact_description})` : "";
          addEmail(info.contact_value, `${tr("Primary Contact")}${desc}`);
        }
      });
    }

    return list;
  }, [customerData, customerEmail, tr]);

  const defaultRecipientEmails = useMemo(() => {
    const billingEmails: string[] = [...(customerData?.billing_emails || [])];
    if (customerData?.is_billing && customerData?.email) {
      billingEmails.push(customerData.email);
    }
    [customerData?.extended_contacts, customerData?.primary_contact_info].forEach((contacts) => {
      if (!Array.isArray(contacts)) return;
      contacts.forEach((contact: any) => {
        if (contact.contact_type === "email" && contact.is_billing && contact.contact_value) {
          billingEmails.push(contact.contact_value);
        }
      });
    });
    if (Array.isArray(customerData?.contacts)) {
      customerData.contacts.forEach((contact: any) => {
        if (contact?.is_billing && contact.email) billingEmails.push(contact.email);
      });
    }
    const recipients = parseStatementEmails(billingEmails.join(", "));
    return recipients.length > 0 ? recipients.join(", ") : customerEmail;
  }, [customerData, customerEmail]);

  // Select billing recipients once customer details are resolved.
  useEffect(() => {
    setSelectedEmail(defaultRecipientEmails || "");
  }, [customerId, defaultRecipientEmails]);

  const handleClearEmail = useCallback(() => {
    setSelectedEmail("");
    setIsEmailDropdownOpen(false);
    emailInputRef.current?.focus();
  }, []);

  const handleSelectEmail = useCallback((email: string) => {
    setSelectedEmail((previous) => {
      const recipients = parseStatementEmails(previous);
      if (recipients.some((recipient) => recipient.toLowerCase() === email.trim().toLowerCase())) {
        return previous;
      }
      return [...recipients, email.trim()].join(", ");
    });
    setIsEmailDropdownOpen(false);
    emailInputRef.current?.focus();
  }, []);

  const handleSendEmail = useCallback(
    async (statementOptions: Omit<StatementEmailPayload, "email" | "emails">, recipients = selectedEmail) => {
      const targetEmails = parseStatementEmails(recipients);
      const targetKeys = targetEmails.map((email) => `${customerId}:${email.toLowerCase()}`);
      if (statementOptions.invoice_ids.length === 0) {
        return;
      }
      if (targetEmails.length === 0) {
        showSnackbar(tr("Please enter an email address"));
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (targetEmails.some((email) => !emailRegex.test(email))) {
        showSnackbar(tr("Please enter a valid email address"));
        return;
      }

      // Check every recipient before sending the statement.
      const currentCooldowns = loadStoredCooldowns();
      const now = Date.now();
      if (targetKeys.some((key) => (currentCooldowns[key] || emailCooldowns[key] || 0) > now)) {
        showSnackbar(tr("Please wait before sending another statement to this email"));
        return;
      }

      setIsSendingEmail(true);
      try {
        const payload: StatementEmailPayload = {
          email: targetEmails[0],
          emails: targetEmails,
          ...statementOptions,
        };

        await axios.post(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/company/customers/${customerId}/send-statement`,
          payload
        );

        showSnackbar(tr("Statement sent successfully!"));

        // Set a 1-minute cooldown for each recipient in the successful send.
        const expiryTime = Date.now() + 60 * 1000;
        const updated = {
          ...emailCooldowns,
          ...loadStoredCooldowns(),
        };
        targetKeys.forEach((key) => {
          updated[key] = expiryTime;
        });
        saveStoredCooldowns(updated);
        setEmailCooldowns(updated);
        setCurrentTime(Date.now());
      } catch (error) {
        const message = error.response?.data?.message || tr("Error sending statement");
        showSnackbar(message);
      } finally {
        setIsSendingEmail(false);
      }
    },
    [selectedEmail, customerId, showSnackbar, tr, emailCooldowns]
  );

  // Close dropdown on click outside or Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (emailDropdownRef.current && !emailDropdownRef.current.contains(target)) {
        setIsEmailDropdownOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsEmailDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return {
    selectedEmail,
    setSelectedEmail,
    isEmailDropdownOpen,
    setIsEmailDropdownOpen,
    customerRegisteredEmails,
    isSendingEmail,
    isEmailCooldownActive,
    remainingCooldownSeconds,
    formattedCooldownTime,
    emailCooldowns,
    emailDropdownRef,
    emailInputRef,
    handleClearEmail,
    handleSelectEmail,
    handleSendEmail,
  };
}
